import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription, finalize } from 'rxjs';
import { DateMaskDirective } from '../../directives/date-mask.directive';
import { FormErrors } from '../../common/enums';
import { toCalendarDateString } from '../../common/helpers';
import { LandingPagesService } from '../../services/landing-pages.service';
import { TurnstileTokenService } from '../../services/turnstile-token.service';

@Component({
  selector: 'app-cancellation-request-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    DateMaskDirective,
  ],
  templateUrl: './cancellation-request-dialog.component.html',
  styleUrl: './cancellation-request-dialog.component.scss',
})
export class CancellationRequestDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dobField', { read: ElementRef }) dobField?: ElementRef<HTMLElement>;

  form: FormGroup;
  submitted = false;
  submitting = false;
  success = false;
  error: string | null = null;
  hasTurnstileToken = false;
  readonly successMessage = FormErrors.CancellationSubmitted;
  readonly minDob = new Date(1900, 0, 1);
  readonly maxDob: Date;
  private tokenSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private landingPages: LandingPagesService,
    private turnstileTokenService: TurnstileTokenService,
    private dialogRef: MatDialogRef<CancellationRequestDialogComponent>,
    private ngZone: NgZone,
    @Inject(MAT_DIALOG_DATA) public data: { landingPageId: string }
  ) {
    const yesterday = new Date();
    yesterday.setHours(0, 0, 0, 0);
    yesterday.setDate(yesterday.getDate() - 1);
    this.maxDob = yesterday;

    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      dateOfBirth: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.tokenSub = this.turnstileTokenService.token$.subscribe(token => {
      this.hasTurnstileToken = !!token;
    });
  }

  ngAfterViewInit(): void {
    this.turnstileTokenService.clear();
    this.initializeTurnstile();
    this.matchDobPanelWidth();
  }

  ngOnDestroy(): void {
    this.tokenSub?.unsubscribe();
    this.turnstileTokenService.reset();
  }

  get formFilled(): boolean {
    const firstName = String(this.form.get('firstName')?.value ?? '').trim();
    const lastName = String(this.form.get('lastName')?.value ?? '').trim();
    const dateOfBirth = this.form.get('dateOfBirth')?.value;
    return this.form.valid && !!firstName && !!lastName && !!dateOfBirth;
  }

  get submitDisabled(): boolean {
    return this.submitting || this.success || !this.formFilled || !this.hasTurnstileToken;
  }

  onSubmit(): void {
    if (this.submitDisabled) {
      return;
    }

    const dateOfBirth = toCalendarDateString(this.form.get('dateOfBirth')?.value);
    const turnstileToken = this.turnstileTokenService.getTurnstileToken();
    if (!dateOfBirth || !turnstileToken) {
      return;
    }

    this.submitted = true;
    this.submitting = true;
    this.error = null;
    // Backdrop click and Esc are ignored until the API answers.
    this.dialogRef.disableClose = true;

    const firstName = String(this.form.get('firstName')?.value ?? '').trim();
    const lastName = String(this.form.get('lastName')?.value ?? '').trim();

    this.landingPages.createCancellationRequest(this.data.landingPageId, {
      firstName,
      lastName,
      dateOfBirth,
    }).pipe(
      finalize(() => {
        this.submitting = false;
        this.dialogRef.disableClose = false;
      })
    ).subscribe({
      next: () => {
        this.success = true;
        this.error = null;
        this.turnstileTokenService.clear();
      },
      error: () => {
        this.turnstileTokenService.reset();
        this.error = FormErrors.GenericError;
      },
    });
  }

  close(): void {
    if (this.submitting) {
      return;
    }
    this.dialogRef.close();
  }

  matchDobPanelWidth(): void {
    const width = this.dobField?.nativeElement.getBoundingClientRect().width;
    if (!width) {
      return;
    }
    document.documentElement.style.setProperty('--cancellation-dob-panel-width', `${width}px`);
  }

  private initializeTurnstile(): void {
    (window as any).onTurnstileCancellationSuccess = (token: string) => {
      this.ngZone.run(() => this.turnstileTokenService.updateToken(token));
    };
    (window as any).onTurnstileCancellationError = () => {
      this.ngZone.run(() => this.turnstileTokenService.updateToken(null));
    };
    (window as any).onTurnstileCancellationExpired = () => {
      this.ngZone.run(() => this.turnstileTokenService.updateToken(null));
    };

    setTimeout(() => {
      const container = document.getElementById('turnstile-cancellation');
      if (container && (window as any).turnstile) {
        container.innerHTML = '';
        const widgetId = (window as any).turnstile.render('#turnstile-cancellation', {
          sitekey: '0x4AAAAAABDQhK0UT12Ulx-d',
          // The default widget is a fixed 300px and overflows narrow phones.
          size: container.clientWidth >= 300 ? 'flexible' : 'compact',
          callback: (window as any).onTurnstileCancellationSuccess,
          'expired-callback': (window as any).onTurnstileCancellationExpired,
          'error-callback': (window as any).onTurnstileCancellationError,
          theme: 'light',
        });
        this.turnstileTokenService.setWidgetId(widgetId);
      }
    }, 100);
  }
}